import { createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState } from "_shared/_stores/redux-store";
import { entityMemberApi, EntityMemberPageArgs, EntityMemberScroll } from "_shared/entity-members/_api/entity-member-api";
import type EntityMemberQuery from "_shared/entity-members/_api/queries/entity-member-query";
import EntityMemberSummary from "_shared/entity-members/entity-member-summary";
import { EntityMemberSummaryJSON } from "_shared/entity-members/entity-member-summary-schema";
import Guid from "_shared/guids/guid";

type EntityMemberStoreState = {
  /**
   * The pages of members a scrolling list has loaded, for each entity and search. The key leaves
   * out the page, so that the next page is added to the ones already held. Only a request that
   * names a `scroll` writes here: the other readers of a roster (a count, a search box) ask for
   * one page of their own, and must not replace the pages a list has scrolled through.
   */
  pagesByQuery: {
    entries: Record<string, Paginated<EntityMemberSummaryJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};

const pageKey = (legalEntityId: string, query: EntityMemberQuery): string => `${legalEntityId}?${query.cacheKey}`;

function mergeMemberPages(
  current: Paginated<EntityMemberSummaryJSON> | undefined,
  next: Paginated<EntityMemberSummaryJSON>,
): Paginated<EntityMemberSummaryJSON> {
  if (!current) return next;
  const merged = [...current.data];
  const indexesByUserId = new Map(merged.map((member, index) => [member.userId, index]));
  for (const member of next.data) {
    const existingIndex = indexesByUserId.get(member.userId);
    if (existingIndex === undefined) {
      indexesByUserId.set(member.userId, merged.length);
      merged.push(member);
    } else {
      merged[existingIndex] = member;
    }
  }
  return { data: merged, headers: next.headers };
}

/** What a scrolling list received for one of its pages, whether fetched or answered from the cache. */
export type ScrolledEntityMemberPage = {
  legalEntityId: string;
  query: EntityMemberQuery;
  scroll: EntityMemberScroll;
  page: Paginated<EntityMemberSummaryJSON>;
};

const isScrolling = (args: EntityMemberPageArgs): args is EntityMemberPageArgs & { scroll: EntityMemberScroll } =>
  args.scroll !== undefined;

const initialState: EntityMemberStoreState = {
  pagesByQuery: {
    entries: {},
    loading: {},
    error: {},
  },
};
export const entityMembersSlice = createSlice({
  name: "entityMembers",
  initialState,
  reducers: {
    /**
     * Dispatched by the scrolling list with what its request resolved to, rather than read from
     * the fulfilled action: a page RTK Query answers from its cache dispatches no fulfilled action,
     * so a list reopened on a cached first page would otherwise never be given it.
     */
    scrolledEntityMemberPageReceived: (state, action: PayloadAction<ScrolledEntityMemberPage>) => {
      const { legalEntityId, query, scroll, page } = action.payload;
      const key = pageKey(legalEntityId, query);
      state.pagesByQuery.entries[key] =
        scroll === "next" ? mergeMemberPages(state.pagesByQuery.entries[key], page) : page;
      state.pagesByQuery.loading[key] = false;
      state.pagesByQuery.error[key] = null;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(entityMemberApi.endpoints.getEntityMemberPage.matchPending, (state, action) => {
      if (!isScrolling(action.meta.arg.originalArgs)) return;
      const { legalEntityId, query } = action.meta.arg.originalArgs;
      const key = pageKey(legalEntityId, query);
      state.pagesByQuery.loading[key] = true;
      state.pagesByQuery.error[key] = null;
    });
    builder.addMatcher(entityMemberApi.endpoints.getEntityMemberPage.matchRejected, (state, action) => {
      if (!isScrolling(action.meta.arg.originalArgs)) return;
      const { legalEntityId, query } = action.meta.arg.originalArgs;
      const key = pageKey(legalEntityId, query);
      state.pagesByQuery.loading[key] = false;
      state.pagesByQuery.error[key] = action.error;
    });
  },
});

export const { scrolledEntityMemberPageReceived } = entityMembersSlice.actions;

const selectMemberPagesByKey = createSelector(
  [(state: RootState) => state.entityMembers.pagesByQuery.entries, (_: RootState, key: string) => key],
  (entries, key) => {
    const pages = entries[key];
    if (!pages) return PaginatedResponse.empty<EntityMemberSummary>();
    return PaginatedResponse.from(EntityMemberSummary.fromJSONArray(pages.data), pages.headers);
  },
);

/** The members a scrolling list has loaded so far for one entity and search. */
export const getEntityMemberPagesByQuery = (legalEntityId: Guid, query: EntityMemberQuery) => (state: RootState) =>
  selectMemberPagesByKey(state, pageKey(legalEntityId.value, query));

export const getIsLoadingEntityMemberPagesByQuery =
  (legalEntityId: Guid, query: EntityMemberQuery) => (state: RootState) =>
    state.entityMembers.pagesByQuery.loading[pageKey(legalEntityId.value, query)] ?? false;

export const getErrorLoadingEntityMemberPagesByQuery =
  (legalEntityId: Guid, query: EntityMemberQuery) => (state: RootState) =>
    state.entityMembers.pagesByQuery.error[pageKey(legalEntityId.value, query)] ?? null;
